import React from 'react';
import { Typography, theme } from 'antd';
import { RiCheckboxBlankCircleFill } from 'react-icons/ri';
import PatientInfoCard from '../clinical/common/PatientInfoCard';
import FileUploader from '../uploader/FileUploader.component';
import { isSectionEnabled, isQuestionEnabled, isAnswerEnabled } from './utils/previewHelpers';
import { SectionRenderer } from './common/SectionRenderer';
import {
  resolveSelectStoredValue,
  resolveChildOptionsStoredValue,
} from '../../utils/formDataTransformer';

const { Title, Text } = Typography;

/**
 * FormResponseView - Read-only view for form responses
 * @param {Object} formSchema - Form schema
 * @param {Object} formValues - Form values
 * @param {string} patientFullName - Patient full name
 * @param {string} patientDateOfBirth - Patient date of birth
 * @param {string|number} patientYearOfBirth - Patient year of birth (fallback)
 * @param {string} patientId - FHIR Patient ID
 */
const FormResponseView = ({ 
  formSchema, 
  formValues,
  patientFullName,
  patientDateOfBirth,
  patientYearOfBirth,
  patientId
}) => {
  const { token } = theme.useToken();
  
  if (!formSchema?.pages) {
    return <Text type="secondary">No form data available</Text>;
  }

  // Process nested children - keyPrefix ensures unique child IDs (matches QuestionRenderers.js logic)
  const processNestedChildren = (children, storeKey, keyPrefix, owningQuestion) => {
    if (!children?.length) return [];
    
    const nestedAnswers = [];
    children.forEach((child, ci) => {
      const childId = child.id || child.concept || child.conceptCode || `${keyPrefix}_child_${ci}`;
      
      if (child.componentType) {
        const fieldId = `${storeKey}_${childId}`;
        const childValue = formValues[fieldId] || formValues[storeKey]?.[childId];
        
        if (!childValue) return;
        
        let displayValue = childValue;
        if (typeof childValue === 'object' && childValue !== null) {
          if (childValue.value !== undefined) {
            displayValue = childValue.value;
            if (childValue.otherValue) {
              displayValue = `${displayValue}: ${childValue.otherValue}`;
            }
          } else if (childValue.otherValue !== undefined) {
            displayValue = childValue.otherValue;
          } else if (Object.keys(childValue).length > 0) {
            const meaningfulKeys = ['label', 'text', 'name', 'display', 'value'];
            const foundKey = meaningfulKeys.find(key => childValue[key]);
            displayValue = foundKey ? childValue[foundKey] : JSON.stringify(childValue);
          }
        }

        if (typeof displayValue === 'string' && owningQuestion) {
          const fromOptions = resolveChildOptionsStoredValue(child, displayValue);
          if (fromOptions != null) {
            displayValue = fromOptions;
          } else {
            const fromQuestionSelect = resolveSelectStoredValue(owningQuestion, displayValue);
            if (fromQuestionSelect) {
              displayValue = fromQuestionSelect.answerLabel;
            }
          }
        }
        
        const childAnswer = {
          answerLabel: child.label || child.componentType,
          terminology: child.terminology,
          nestedValue: displayValue,
          children: []
        };
        
        if (child.children?.length > 0) {
          childAnswer.children = processNestedChildren(child.children, fieldId, childId, owningQuestion);
        }
        
        nestedAnswers.push(childAnswer);
      } else {
        const childValue = formValues[storeKey]?.[childId];
        
        if (childValue === true || (typeof childValue === 'object' && childValue !== null && childValue.checked !== false)) {
          const childAnswer = {
            answerLabel: child.label,
            terminology: child.terminology,
            children: []
          };
          
          if (child.children?.length > 0) {
            childAnswer.children = processNestedChildren(child.children, storeKey, childId, owningQuestion);
          }
          
          nestedAnswers.push(childAnswer);
        }
      }
    });
    
    return nestedAnswers;
  };

  const renderAnswer = (answer, isNested = false) => {
    return (
      <div style={{ marginBottom: isNested ? 2 : 8 }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: 8
        }}>
          <RiCheckboxBlankCircleFill style={{ color: token.colorPrimary, marginTop: 2, fontSize: 12, flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <Text style={{ fontWeight: isNested ? 'normal' : '500' }}>{answer.answerLabel}</Text>
            {answer.terminology && (
              <Text type="secondary" style={{ fontSize: 11, marginLeft: 8, fontFamily: 'monospace' }}>
                ({answer.terminology.code})
              </Text>
            )}
            {answer.nestedValue && (
              <div style={{ marginTop: 4, marginLeft: 20, fontSize: 12, color: '#8c8c8c' }}>
                → {answer.nestedValue}
              </div>
            )}
          </div>
        </div>
        {answer.children && answer.children.length > 0 && (
          <ul style={{ 
            marginTop: 4, 
            marginBottom: 0, 
            paddingLeft: 24,
            listStyleType: 'none'
          }}>
            {answer.children.map((child, idx) => (
              <li key={idx} style={{ marginBottom: 4, listStyle: 'none' }}>
                {renderAnswer(child, true)}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const renderQuestion = (question, formSchema) => {
    // Check if question should be displayed based on enableWhen conditions
    if (!isQuestionEnabled(question, formValues, formSchema)) {
      return null;
    }

    const questionValue = formValues?.[question.id];
    if (!questionValue || (typeof questionValue === 'object' && Object.keys(questionValue).length === 0)) {
      return null;
    }

    const rendering = question.questionOptions?.rendering;
    const answers = [];

    switch (rendering) {
      case 'checkbox':
        question.questionOptions?.answers?.forEach((answer, idx) => {
          // Check if answer should be displayed based on enableWhen conditions
          if (!isAnswerEnabled(answer, question, question.questionOptions?.answers, 'checkbox', formValues)) {
            return;
          }
          
          const answerId = answer.concept || answer.conceptCode || `answer_${idx}`;
          if (questionValue[answerId]) {
            const answerData = {
              answerLabel: answer.label,
              terminology: answer.terminology,
              children: []
            };
            
            if (answer.children?.length > 0) {
              answerData.children = processNestedChildren(answer.children, question.id, answerId, question);
            }
            
            answers.push(answerData);
          }
        });
        
        const otherValue = questionValue['__other__'];
        if (otherValue?.checked) {
          answers.push({
            answerLabel: question.questionOptions?.otherLabel || 'Other',
            nestedValue: otherValue.otherValue || '(not specified)'
          });
        }
        break;

      case 'radio':
        const radioValue = questionValue;
        const isOtherSelected = typeof radioValue === 'object' && radioValue?.value === '__other__';
        
        if (isOtherSelected) {
          answers.push({
            answerLabel: question.questionOptions?.otherLabel || 'Other',
            nestedValue: radioValue?.otherValue || '(not specified)'
          });
        } else {
          let selectedAnswerId = null;
          const selectedAnswer = question.questionOptions?.answers?.find((answer, idx) => {
            const answerId = answer.concept || answer.conceptCode || `answer_${idx}`;
            if (answerId === radioValue) {
              selectedAnswerId = answerId;
              return true;
            }
            return false;
          });
          if (selectedAnswer) {
            if (isAnswerEnabled(selectedAnswer, question, question.questionOptions?.answers, 'radio', formValues)) {
              const answerData = {
                answerLabel: selectedAnswer.label,
                terminology: selectedAnswer.terminology,
                children: []
              };
              
              if (selectedAnswer.children?.length > 0) {
                const childrenStoreKey = `${question.id}_children`;
                answerData.children = processNestedChildren(
                  selectedAnswer.children,
                  childrenStoreKey,
                  selectedAnswerId,
                  question
                );
              }
              
              answers.push(answerData);
            }
          }
        }
        break;

      case 'select':
        const selectValue = questionValue;
        const isSelectOther = typeof selectValue === 'object' && selectValue?.value === '__other__';
        const displayValue = typeof selectValue === 'object' ? selectValue?.value : selectValue;
        
        if (isSelectOther) {
          answers.push({
            answerLabel: question.questionOptions?.otherLabel || 'Other',
            nestedValue: selectValue?.otherValue || '(not specified)'
          });
        } else {
          // First try to find a top-level answer match
          let selectedAnswer = question.questionOptions?.answers?.find((answer, idx) => {
            const answerId = answer.concept || answer.conceptCode || `answer_${idx}`;
            return answerId === displayValue;
          });
          
          // If not found, search through children (for select with OptGroups)
          if (!selectedAnswer) {
            question.questionOptions?.answers?.forEach((answer, answerIdx) => {
              const answerId = answer.concept || answer.conceptCode || `answer_${answerIdx}`;
              if (answer.children?.length > 0) {
                answer.children.forEach((child, childIdx) => {
                  const childId = child.concept || child.conceptCode || `${answerId}_child_${childIdx}`;
                  if (childId === displayValue) {
                    // Found a child match - display parent label + child label (matching select dropdown format)
                    answers.push({
                      answerLabel: `${answer.label} ${child.label}`,
                      terminology: child.terminology || answer.terminology
                    });
                  }
                });
              }
            });
          } else {
            // Found top-level answer match
            // Check if answer should be displayed based on enableWhen conditions
            if (isAnswerEnabled(selectedAnswer, question, question.questionOptions?.answers, 'select', formValues)) {
              answers.push({
                answerLabel: selectedAnswer.label,
                terminology: selectedAnswer.terminology
              });
            }
          }
          
          // If still no match found, display raw value as fallback
          if (answers.length === 0) {
            return (
              <div style={{ marginBottom: 20 }}>
                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                  {question.label}
                </Text>
                <Text style={{ marginLeft: 16 }}>{displayValue}</Text>
              </div>
            );
          }
        }
        break;

      case 'checkbox-with-select':
        question.questionOptions?.answers?.forEach((answer, idx) => {
          const answerId = answer.checkboxConceptCode || answer.checkboxTerminology?.id || `checkbox-select_${idx}`;
          const checkboxValue = questionValue[answerId];
          
          if (checkboxValue?.checked) {
            const selectValue = checkboxValue?.selectValue;
            const isOther = typeof selectValue === 'object' && selectValue?.value === '__other__';
            
            let nestedValue = null;
            if (selectValue) {
              if (isOther) {
                nestedValue = `${question.questionOptions?.otherLabel || 'Other'}: ${selectValue?.otherValue || '(not specified)'}`;
              } else {
                const selectedOption = answer.selectOptions?.find(opt => 
                  (opt.label || `option_${idx}`) === selectValue
                );
                nestedValue = selectedOption?.label || selectValue;
              }
            }
            
            answers.push({
              answerLabel: answer.checkboxLabel || answer.checkboxTerminology?.display || 'Checkbox Label',
              terminology: answer.checkboxTerminology,
              nestedValue
            });
          }
        });
        break;

      case 'file': {
        const fileList = Array.isArray(questionValue) ? questionValue : [];
        if (!fileList.length) return null;
        return (
          <div style={{ marginBottom: 20 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {question.label}
            </Text>
            <div style={{ marginLeft: 16 }}>
              <FileUploader value={fileList} readonly />
            </div>
          </div>
        );
      }

      default:
        // Handle objects that don't match known rendering types
        let defaultDisplayValue = questionValue;
        if (typeof questionValue === 'object' && questionValue !== null) {
          if (questionValue.value !== undefined) {
            defaultDisplayValue = questionValue.value;
            if (questionValue.otherValue) {
              defaultDisplayValue = `${defaultDisplayValue}: ${questionValue.otherValue}`;
            }
          } else if (questionValue.otherValue !== undefined) {
            defaultDisplayValue = questionValue.otherValue;
          } else if (Object.keys(questionValue).length > 0) {
            const meaningfulKeys = ['label', 'text', 'name', 'display', 'value'];
            const foundKey = meaningfulKeys.find(key => questionValue[key]);
            defaultDisplayValue = foundKey ? questionValue[foundKey] : JSON.stringify(questionValue);
          } else {
            return null;
          }
        }
        
        return (
          <div style={{ marginBottom: 20 }}>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>
              {question.label}
            </Text>
            <Text style={{ marginLeft: 16 }}>{defaultDisplayValue}</Text>
          </div>
        );
    }

    if (answers.length === 0) return null;

    const layout = question.questionOptions?.layout || 'vertical';
    const isHorizontal = layout === 'horizontal';

    return (
      <div style={{ marginBottom: 20 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {question.label}
        </Text>
        <div style={{
          display: 'flex',
          flexDirection: isHorizontal ? 'row' : 'column',
          flexWrap: isHorizontal ? 'wrap' : 'nowrap',
          gap: isHorizontal ? '16px' : '0px'
        }}>
          {answers.map((answer, idx) => (
            <React.Fragment key={idx}>
              {renderAnswer(answer)}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // Check if a section has any questions with values (before rendering)
  const sectionHasContent = (section) => {
    if (!isSectionEnabled(section, formSchema, formValues)) {
      return false;
    }
    const questions = section.questions || [];
    return questions.some(question => {
      if (!isQuestionEnabled(question, formValues, formSchema)) {
        return false;
      }
      const questionValue = formValues?.[question.id];
      if (!questionValue) return false;
      if (typeof questionValue === 'object' && Object.keys(questionValue).length === 0) return false;
      return true;
    });
  };

  // Check if a page has any sections with content
  const pageHasContent = (page) => {
    const sections = page.sections || [];
    return sections.some(section => sectionHasContent(section));
  };

  // Filter pages to only those with content
  const pagesWithContent = formSchema.pages.filter(page => pageHasContent(page));
  const isSinglePage = pagesWithContent.length === 1;

  return (
    <div style={{ padding: '0 8px' }}>
      <PatientInfoCard
        patientFullName={patientFullName}
        patientDateOfBirth={patientDateOfBirth}
        patientYearOfBirth={patientYearOfBirth}
        showCard={true}
        style={{ marginBottom: 24 }}
      />
      {pagesWithContent.map((page, pageIdx) => {
        // Filter sections to only those with content
        const sectionsWithContent = (page.sections || []).filter(section => sectionHasContent(section));
        
        return (
          <div key={pageIdx} style={{ marginBottom: pageIdx < pagesWithContent.length - 1 ? 32 : 0 }}>
            {/* Page Header - only show if multiple pages with content */}
            {!isSinglePage && page.label && (
              <div style={{ 
                marginBottom: 16,
                paddingBottom: 8,
                borderBottom: `1px solid ${token.colorBorderSecondary}`
              }}>
                <Title level={3} style={{ margin: 0, color: token.colorText }}>
                  {page.label}
                </Title>
              </div>
            )}
            {/* Page-level free text (if any) */}
            {page.freeText && (
              <div 
                style={{ 
                  marginBottom: 16,
                  padding: 8,
                  backgroundColor: 'transparent'
                }}
                dangerouslySetInnerHTML={{ __html: page.freeText }}
              />
            )}
            {/* Page Sections */}
            {sectionsWithContent.map((section, sectionIndex) => (
              <SectionRenderer
                key={sectionIndex}
                section={section}
                sectionIndex={sectionIndex}
                renderQuestion={(q) => renderQuestion(q, formSchema)}
                compact={false}
                schema={formSchema}
                formValues={formValues}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default FormResponseView;

